import { Controller } from "@hotwired/stimulus";

// Player de quiz web (QZ-501) — enregistré dans learn.js UNIQUEMENT.
//
// Toutes les questions sont dans le payload embarqué : naviguer = 0 requête.
// Le feedback local (training) utilise les solutions du payload ; la
// correction qui COMPTE est toujours serveur.
//
// DEUX MODES d'envoi, selon les valeurs passées par la vue :
//
//  - TENTATIVE (quiz) — `answersUrl` + `finalizeUrl`. Autosave : chaque
//    réponse part en fetch JSON ; en cas d'échec réseau elle reste dans un
//    buffer localStorage rejoué à la prochaine occasion (retour réseau,
//    question suivante, finalisation). La tentative existe déjà côté serveur.
//
//  - SESSION (révision Leitner) — `sessionUrl` + `sessionId`. Il n'y a PAS de
//    tentative serveur à alimenter au fil de l'eau : le moteur Leitner attend
//    UN seul appel idempotent portant toutes les réponses (il promeut les
//    boîtes de mémorisation, opération non rejouable). On ne pousse donc rien
//    en cours de route ; le buffer localStorage sert de filet en cas de
//    rechargement, et tout part à la finalisation.
export default class extends Controller {
  static targets = ["questions", "feedback", "progressBar", "progressText",
                    "timer", "syncStatus", "prevBtn", "mainBtn", "i18n"];
  static values = { payload: Object, answersUrl: String, finalizeUrl: String,
                    sessionUrl: String, sessionId: String,
                    storageKey: String, nursery: Boolean };

  connect() {
    this.t = JSON.parse(this.i18nTarget.textContent);
    this.quiz = this.payloadValue.quiz;
    this.questions = this.payloadValue.questions;
    this.responses = { ...this.payloadValue.answers };
    this.checked = {};
    this.index = 0;
    this.finalized = false;

    this.renderAll();
    this.goTo(this.firstUnanswered());
    this.startTimer();
    this.flushBuffer();
    window.addEventListener("online", () => this.flushBuffer());
  }

  // ---- Rendu ---------------------------------------------------------------

  renderAll() {
    this.questionsTarget.innerHTML = "";
    this.questions.forEach((q, i) => {
      const panel = document.createElement("div");
      panel.dataset.index = i;
      panel.hidden = true;
      panel.className = "quiz-question";
      panel.appendChild(this.renderQuestion(q, i));
      this.questionsTarget.appendChild(panel);
    });
  }

  renderQuestion(q) {
    const wrap = document.createElement("div");
    wrap.className = "ds-card p-4";
    const statement = document.createElement("p");
    statement.className = "font-medium text-lg mb-4";
    statement.textContent = q.content.statement?.text || "";
    wrap.appendChild(statement);

    const saved = this.responses[q.id];
    if (q.type === "single_choice" || q.type === "multiple_choice") {
      (q.content.choices || []).forEach((choice) => {
        wrap.appendChild(this.renderChoice(q, choice, saved));
      });
    } else if (q.type === "true_false") {
      [[true, this.t.true_label], [false, this.t.false_label]].forEach(([val, label]) => {
        wrap.appendChild(this.renderBool(q, val, label, saved));
      });
    } else if (q.type === "short_answer") {
      const input = document.createElement("input");
      input.type = "text";
      input.className = "ds-input w-full text-lg";
      input.placeholder = this.t.short_answer_placeholder;
      input.value = saved?.text ?? "";
      input.addEventListener("input", () => this.setResponse(q.id, { text: input.value }));
      wrap.appendChild(input);
    } else if (q.type === "numeric") {
      const input = document.createElement("input");
      input.type = "text";
      input.inputMode = "decimal";
      input.className = "ds-input w-full text-lg";
      input.placeholder = this.t.numeric_placeholder;
      if (q.content.unit) input.placeholder += ` (${q.content.unit})`;
      input.value = saved?.value ?? "";
      input.addEventListener("input", () => this.setResponse(q.id, { value: input.value }));
      wrap.appendChild(input);
    } else if (q.type === "image_choice") {
      wrap.appendChild(this.renderImageChoice(q, saved));
    } else if (q.type === "ordering") {
      wrap.appendChild(this.renderOrdering(q, saved));
    } else if (q.type === "matching") {
      wrap.appendChild(this.renderMatching(q, saved));
    } else if (q.type === "cloze") {
      wrap.appendChild(this.renderCloze(q, saved));
    }

    if (q.content.hint) {
      const hint = document.createElement("details");
      hint.className = "mt-3 text-sm text-amber-700";
      const summary = document.createElement("summary");
      summary.textContent = "💡";
      hint.appendChild(summary);
      hint.appendChild(document.createTextNode(q.content.hint));
      hint.addEventListener("toggle", () => { if (hint.open) this.hintUsed(q.id); });
      wrap.appendChild(hint);
    }
    return wrap;
  }

  renderChoice(q, choice, saved) {
    const multiple = q.type === "multiple_choice";
    const label = document.createElement("label");
    label.className = "quiz-choice flex items-center gap-3 p-3 mb-2 rounded-[12px] border border-gray-200 cursor-pointer";
    const input = document.createElement("input");
    input.type = multiple ? "checkbox" : "radio";
    input.name = `q-${q.id}`;
    input.value = choice.key;
    const selected = saved?.selected;
    input.checked = multiple ? (selected || []).includes(choice.key) : selected === choice.key;
    input.addEventListener("change", () => {
      const inputs = [...this.currentPanel().querySelectorAll(`input[name='q-${q.id}']:checked`)];
      const keys = inputs.map((i) => i.value);
      this.setResponse(q.id, { selected: multiple ? keys : keys[0] });
    });
    const span = document.createElement("span");
    span.textContent = choice.text || "";
    label.append(input, span);
    return label;
  }

  renderBool(q, val, text, saved) {
    const label = document.createElement("label");
    label.className = "quiz-choice flex items-center gap-3 p-3 mb-2 rounded-[12px] border border-gray-200 cursor-pointer";
    const input = document.createElement("input");
    input.type = "radio";
    input.name = `q-${q.id}`;
    input.checked = saved?.selected === val;
    input.addEventListener("change", () => this.setResponse(q.id, { selected: val }));
    const span = document.createElement("span");
    span.textContent = text;
    label.append(input, span);
    return label;
  }

  // ---- Types visuels (QZ-1103, maternelle) ---------------------------------

  // Sélection d'image : mêmes réponses que single_choice ({selected}), rendu
  // en tuiles image. L'URL vient de `choice.media_url` (injectée par le pack
  // média QZ-1104) ; à défaut on retombe sur le texte, jamais d'écran vide.
  renderImageChoice(q, saved) {
    const grid = document.createElement("div");
    grid.className = "quiz-image-grid";
    (q.content.choices || []).forEach((choice) => {
      const label = document.createElement("label");
      label.className = "quiz-image-choice";
      const input = document.createElement("input");
      input.type = "radio";
      input.name = `q-${q.id}`;
      input.value = choice.key;
      input.className = "sr-only";
      input.checked = saved?.selected === choice.key;
      input.addEventListener("change", () => this.setResponse(q.id, { selected: choice.key }));
      if (choice.media_url) {
        const img = document.createElement("img");
        img.src = choice.media_url;
        img.alt = choice.text || "";
        img.loading = "lazy";
        label.appendChild(img);
      }
      if (choice.text || !choice.media_url) {
        const caption = document.createElement("span");
        caption.className = "quiz-image-choice__caption";
        caption.textContent = choice.text || "";
        label.appendChild(caption);
      }
      label.prepend(input);
      grid.appendChild(label);
    });
    return grid;
  }

  // Remettre dans l'ordre : boutons monter/descendre (robuste sur Android bas
  // de gamme, pas de drag). Réponse {order:[clés]}.
  renderOrdering(q, saved) {
    const list = document.createElement("ol");
    list.className = "quiz-ordering";
    const items = q.content.items || [];
    let order = saved?.order?.filter((k) => items.some((i) => i.key === k)) || items.map((i) => i.key);
    // Complète l'ordre si des items manquent (payload mélangé côté serveur).
    items.forEach((i) => { if (!order.includes(i.key)) order.push(i.key); });

    const rerender = () => {
      list.innerHTML = "";
      order.forEach((key, pos) => {
        const item = items.find((i) => i.key === key);
        const li = document.createElement("li");
        li.className = "quiz-ordering__item";
        const text = document.createElement("span");
        text.textContent = item?.text || key;
        const controls = document.createElement("span");
        controls.className = "quiz-ordering__controls";
        const up = this.arrowBtn("▲", pos === 0, () => { this.swap(order, pos, pos - 1); commit(); });
        const down = this.arrowBtn("▼", pos === order.length - 1, () => { this.swap(order, pos, pos + 1); commit(); });
        controls.append(up, down);
        li.append(text, controls);
        list.appendChild(li);
      });
    };
    const commit = () => { this.setResponse(q.id, { order: [...order] }); rerender(); };
    rerender();
    return list;
  }

  arrowBtn(glyph, disabled, onClick) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "quiz-ordering__btn";
    btn.textContent = glyph;
    btn.disabled = disabled;
    btn.addEventListener("click", onClick);
    return btn;
  }

  swap(arr, a, b) {
    if (b < 0 || b >= arr.length) return;
    [arr[a], arr[b]] = [arr[b], arr[a]];
  }

  // Association : un menu déroulant par élément de gauche (pas de drag).
  // Réponse {pairs:{"l1":"r2"}}.
  renderMatching(q, saved) {
    const table = document.createElement("div");
    table.className = "quiz-matching";
    const pairs = { ...(saved?.pairs || {}) };
    (q.content.left || []).forEach((left) => {
      const row = document.createElement("div");
      row.className = "quiz-matching__row";
      const label = document.createElement("span");
      label.className = "quiz-matching__left";
      label.textContent = left.text || left.key;
      const select = document.createElement("select");
      select.className = "ds-input quiz-matching__select";
      const blank = document.createElement("option");
      blank.value = "";
      blank.textContent = this.t.matching_pick || "—";
      select.appendChild(blank);
      (q.content.right || []).forEach((right) => {
        const opt = document.createElement("option");
        opt.value = right.key;
        opt.textContent = right.text || right.key;
        opt.selected = pairs[left.key] === right.key;
        select.appendChild(opt);
      });
      select.addEventListener("change", () => {
        if (select.value) { pairs[left.key] = select.value; } else { delete pairs[left.key]; }
        this.setResponse(q.id, { pairs: { ...pairs } });
      });
      row.append(label, select);
      table.appendChild(row);
    });
    return table;
  }

  // Texte à trous : les [clé] du texte deviennent des champs de saisie.
  // Réponse {blanks:{"b1":"mange"}}.
  renderCloze(q, saved) {
    const container = document.createElement("p");
    container.className = "quiz-cloze";
    const blanks = { ...(saved?.blanks || {}) };
    const parts = String(q.content.text || "").split(/(\[[^\]]+\])/);
    parts.forEach((part) => {
      const match = part.match(/^\[([^\]]+)\]$/);
      if (match) {
        const key = match[1];
        const input = document.createElement("input");
        input.type = "text";
        input.className = "ds-input quiz-cloze__blank";
        input.value = blanks[key] || "";
        input.setAttribute("aria-label", key);
        input.addEventListener("input", () => {
          blanks[key] = input.value;
          this.setResponse(q.id, { blanks: { ...blanks } });
        });
        container.appendChild(input);
      } else if (part) {
        container.appendChild(document.createTextNode(part));
      }
    });
    return container;
  }

  // ---- Navigation ----------------------------------------------------------

  goTo(index) {
    this.index = Math.max(0, Math.min(index, this.questions.length - 1));
    [...this.questionsTarget.children].forEach((panel, i) => { panel.hidden = i !== this.index; });
    this.feedbackTarget.hidden = true;
    const q = this.questions[this.index];
    const isChecked = this.checked[q.id];
    const last = this.index === this.questions.length - 1;
    this.mainBtnTarget.textContent = this.immediate() && !isChecked
      ? this.t.check
      : (last ? this.t.finish : this.t.next);
    this.prevBtnTarget.disabled = this.index === 0;
    this.updateProgress();
  }

  previous() { this.goTo(this.index - 1); }

  main() {
    const q = this.questions[this.index];
    if (this.immediate() && !this.checked[q.id]) {
      this.check(q);
      return;
    }
    if (this.index === this.questions.length - 1) {
      this.finalize();
    } else {
      this.goTo(this.index + 1);
    }
  }

  // Vérification locale (mode training/revision : la solution est embarquée).
  check(q) {
    this.checked[q.id] = true;
    this.pushAnswer(q.id);
    const solution = this.solutionFor(q);
    const good = this.isLocallyCorrect(q);
    this.feedbackTarget.hidden = false;
    this.feedbackTarget.className = `ds-alert mt-3 ${good ? "ds-alert--success" : "ds-alert--warning"}`;
    let text = good ? this.t.correct : this.t.incorrect;
    const explanation = this.explanationFor(q.id);
    if (!good && explanation) text += ` — ${explanation}`;
    if (!good && solution !== null && q.type === "numeric") text += ` (${solution})`;
    this.feedbackTarget.textContent = text;
    const last = this.index === this.questions.length - 1;
    this.mainBtnTarget.textContent = last ? this.t.finish : this.t.next;
  }

  // ---- Réponses & sync -----------------------------------------------------

  setResponse(qid, response) {
    this.responses[qid] = response;
    delete this.checked[qid];
    this.updateProgress();
    if (!this.immediate()) this.pushAnswer(qid);
  }

  hintUsed(qid) {
    this.hints ||= {};
    this.hints[qid] = true;
  }

  pushAnswer(qid) {
    if (this.previewMode()) return;

    const entry = {
      question_id: qid,
      response: this.responses[qid] || {},
      answered_at: new Date().toISOString(),
      hint_used: !!this.hints?.[qid],
    };
    this.buffer().push(entry);
    this.saveBuffer();
    this.flushBuffer();
  }

  async flushBuffer() {
    // Mode session : rien ne part avant la fin (l'appel Leitner est unique et
    // promeut les boîtes — l'envoyer par morceaux promouvrait plusieurs fois).
    if (this.sessionMode() || this.previewMode() || this.flushing) return;
    const pending = this.buffer();
    if (pending.length === 0) return;

    this.flushing = true;
    // Instantané de CE qu'on envoie : l'élève peut répondre à une autre
    // question pendant le vol (pushAnswer ajoute en fin de buffer) — on ne
    // retirera QUE les entrées effectivement envoyées, jamais celles enfilées
    // entre-temps (sinon perte de réponse sur réseau mobile lent).
    const sent = pending.length;
    let flushedOk = false;
    try {
      const response = await fetch(this.answersUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ answers: pending.slice(0, sent) }),
      });
      if (response.ok) {
        this.pendingBuffer = this.buffer().slice(sent);
        this.saveBuffer();
        this.setSync("ok");
        flushedOk = true;
      } else {
        this.setSync("offline");
      }
    } catch {
      this.setSync("offline");
    } finally {
      this.flushing = false;
    }
    // Réponses enfilées pendant un vol RÉUSSI : rattrapage immédiat (jamais
    // après un échec — le buffer est conservé et repartira au prochain push).
    if (flushedOk && this.buffer().length > 0) this.flushBuffer();
  }

  async finalize() {
    if (this.finalized) return;
    if (this.previewMode()) {
      this.feedbackTarget.hidden = false;
      this.feedbackTarget.className = "ds-alert ds-alert--info mt-3";
      this.feedbackTarget.textContent = this.t.preview_end;
      return;
    }

    this.mainBtnTarget.disabled = true;
    this.mainBtnTarget.textContent = this.t.finalizing;
    await this.flushBuffer();
    try {
      const response = await fetch(
        this.sessionMode() ? this.sessionUrlValue : this.finalizeUrlValue,
        {
          method: "POST",
          headers: this.headers(),
          // Mode session : toutes les réponses en un seul envoi idempotent,
          // identifié par l'uuid client. Les clés en trop du buffer
          // (answered_at, hint_used…) sont ignorées par le `permit` serveur.
          body: this.sessionMode()
            ? JSON.stringify({
                client_session_id: this.sessionIdValue,
                answers: this.buffer(),
              })
            : "{}",
        },
      );
      const body = await response.json();
      if (response.ok && body.data?.result_url) {
        this.finalized = true;
        localStorage.removeItem(this.storageKeyValue);
        await this.celebrate(body.data.gamification);
        window.location.assign(body.data.result_url);
        return;
      }
    } catch { /* réseau coupé : on retente */ }
    this.mainBtnTarget.disabled = false;
    this.mainBtnTarget.textContent = this.t.finish;
    // En mode session, le retour du réseau ne renverra RIEN tout seul
    // (flushBuffer est neutralisé) : il faut inviter à réessayer, et surtout ne
    // pas afficher « c'est gardé, ça partira au retour du réseau ».
    this.setSync(this.sessionMode() ? "failed" : "offline");
  }

  // Célébration de fin (QZ-1102) : XP gagnés + nouveaux badges, ~1,5 s, ou
  // clic pour continuer. No-op si la gamification est désactivée (delta null).
  celebrate(gamification) {
    if (!gamification || (!gamification.xp_gained && !gamification.new_badges?.length)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const overlay = document.createElement("div");
      overlay.className = "quiz-celebration";

      if (gamification.xp_gained) {
        const xp = document.createElement("p");
        xp.className = "quiz-celebration__xp";
        xp.textContent = `+${gamification.xp_gained} XP`;
        overlay.appendChild(xp);
      }
      (gamification.new_badges || []).forEach((badge) => {
        const chip = document.createElement("span");
        chip.className = "quiz-celebration__badge";
        chip.textContent = `★ ${badge.name}`;
        overlay.appendChild(chip);
      });
      const hint = document.createElement("p");
      hint.className = "text-sm text-gray-400 mt-2";
      hint.textContent = this.t.tap_continue || "";
      overlay.appendChild(hint);

      const done = () => resolve();
      overlay.addEventListener("click", done);
      document.body.appendChild(overlay);
      setTimeout(done, 1600);
    });
  }

  // ---- Utilitaires ---------------------------------------------------------

  buffer() {
    if (!this.pendingBuffer) {
      try {
        this.pendingBuffer = JSON.parse(localStorage.getItem(this.storageKeyValue) || "[]");
      } catch { this.pendingBuffer = []; }
    }
    return this.pendingBuffer;
  }

  saveBuffer() {
    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(this.pendingBuffer || []));
    } catch {
      // Quota localStorage saturé, ou navigation privée : on garde le tampon en
      // mémoire et on continue. Sans cette garde, l'exception remontait jusqu'au
      // gestionnaire de clic et le bouton « Vérifier » cessait de répondre, sans
      // le moindre message. `buffer()` protégeait déjà la LECTURE.
    }
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
    };
  }

  immediate() { return this.quiz.correction_policy === "immediate"; }
  // Aperçu enseignant, ou vue sans aucune destination d'envoi : on joue mais
  // rien n'est enregistré.
  previewMode() {
    return this.quiz.preview || (!this.answersUrlValue && !this.sessionUrlValue);
  }

  sessionMode() { return !!this.sessionUrlValue; }
  currentPanel() { return this.questionsTarget.children[this.index]; }

  firstUnanswered() {
    const i = this.questions.findIndex((q) => !this.responses[q.id]);
    return i === -1 ? 0 : i;
  }

  solutionFor(q) { return q.content.correct ?? null; }
  explanationFor(qid) {
    const q = this.questions.find((x) => x.id === qid);
    return q?.content?.explanation?.text || null;
  }

  isLocallyCorrect(q) {
    const r = this.responses[q.id];
    const correct = q.content.correct;
    if (r == null) return false;
    // short_answer : la solution vit dans `accepted`, pas `correct` — le guard
    // `correct == null` marquait TOUTE réponse courte comme fausse (bug QA).
    const normalize = (t) => String(t || "").trim().replace(/\s+/g, " ")
      .normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();
    if (q.type === "short_answer") {
      const accepted = (q.content.accepted || []).map(normalize);
      return accepted.includes(normalize(r.text));
    }
    // Types visuels : solution embarquée en mode immédiat/entraînement.
    if (q.type === "ordering") {
      const target = q.content.correct_order;
      return Array.isArray(target) && JSON.stringify(r.order) === JSON.stringify(target);
    }
    if (q.type === "matching") {
      const target = q.content.pairs || {};
      const keys = Object.keys(target);
      return keys.length > 0 && keys.every((k) => (r.pairs || {})[k] === target[k]);
    }
    if (q.type === "cloze") {
      const defs = q.content.blanks || [];
      return defs.length > 0 && defs.every((b) =>
        (b.accepted || []).map(normalize).includes(normalize((r.blanks || {})[b.key])));
    }
    if (correct == null) return false;
    if (q.type === "multiple_choice") {
      const sel = [...(r.selected || [])].sort().join();
      return sel === [...correct].sort().join();
    }
    if (q.type === "numeric") {
      const value = parseFloat(String(r.value ?? "").replace(",", "."));
      const tolerance = parseFloat(q.content.tolerance ?? 0) || 0;
      return !Number.isNaN(value) && Math.abs(value - parseFloat(correct)) <= tolerance;
    }
    return r.selected === correct;
  }

  updateProgress() {
    const answered = this.questions.filter((q) => this.responses[q.id]).length;
    // Maternelle : zéro chiffre affiché à l'enfant — progression en pastilles.
    if (this.nurseryValue) {
      this.progressTextTarget.textContent =
        "●".repeat(this.index + 1) + "○".repeat(this.questions.length - this.index - 1);
    } else {
      this.progressTextTarget.textContent = `${this.index + 1}/${this.questions.length}`;
    }
    this.progressBarTarget.style.width = `${(answered * 100) / this.questions.length}%`;
  }

  setSync(state) {
    this.syncStatusTarget.textContent = state === "ok" ? "●" : "◌";
    this.syncStatusTarget.className = state === "ok" ? "text-[#47A534]" : "text-amber-500";
    if (state !== "offline" && state !== "failed") return;

    this.feedbackTarget.hidden = false;
    this.feedbackTarget.className =
      state === "failed" ? "ds-alert ds-alert--warning mt-3" : "ds-alert ds-alert--info mt-3";
    this.feedbackTarget.textContent =
      state === "failed" ? this.t.session_send_failed : this.t.answer_saved_offline;
  }

  startTimer() {
    // Maternelle : jamais de chrono visible (cible timer non rendue). Le ≤5 min
    // reste une consigne de conception, pas un compte à rebours pour l'enfant.
    if (this.nurseryValue) return;
    const limit = this.quiz.time_limit_seconds;
    if (!limit) return;

    this.timerTarget.hidden = false;
    const startedAt = Date.now();
    this.timerInterval = setInterval(() => {
      const left = limit - Math.floor((Date.now() - startedAt) / 1000);
      if (left <= 0) {
        clearInterval(this.timerInterval);
        this.finalize();
        return;
      }
      const m = Math.floor(left / 60);
      const s = String(left % 60).padStart(2, "0");
      this.timerTarget.textContent = `${m}:${s}`;
    }, 1000);
  }

  disconnect() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }
}
